import React,{useState,useEffect,useRef,useCallback} from 'react';
import {supabase} from '../../supabaseClient';
import {format} from 'date-fns';
import SafeIcon from '../../common/SafeIcon';
import {processVideoUrl} from '../../utils/videoUtils';

const MessagingSection=({trainerId,trainerName,onUnreadUpdate,initialUnreadMap})=> {
const [plans,setPlans]=useState([]);
const [selectedPlan,setSelectedPlan]=useState('');
const [messages,setMessages]=useState([]);
const [newMessage,setNewMessage]=useState('');
const [loading,setLoading]=useState(true);
const [localUnreadMap,setLocalUnreadMap]=useState(initialUnreadMap || {});
const [tasks,setTasks]=useState({});
const [sending,setSending]=useState(false);
const [deletingId,setDeletingId]=useState(null);
const messagesEndRef=useRef(null);

const fetchPlans=useCallback(async ()=> {
try {
const {data: clients}=await supabase.from('training_clients').select('id').eq('trainer_id',trainerId);
const clientIds=clients?.map(c=> c.id) || [];
const {data: plansData,error}=await supabase
 .from('training_plans')
 .select('id,plan_name,training_clients(id,owner_name,dog_name)')
 .in('client_id',clientIds)
 .order('plan_name');
if (error) throw error;
setPlans(plansData || []);
if (plansData?.length > 0 && !selectedPlan) {
setSelectedPlan(plansData[0].id);
}
} catch (err) {
console.error('Error fetching plans:',err);
} finally {
setLoading(false);
}
},[trainerId,selectedPlan]);

const markAsRead=useCallback(async (planId)=> {
if (!planId) return;
setLocalUnreadMap(prev=> ({...prev,[planId]: 0}));
onUnreadUpdate?.(planId,0);
try {
await supabase
 .from('trainer_messages')
 .update({is_read: true})
 .eq('plan_id',planId)
 .eq('sender_type','owner')
 .eq('is_read',false);
} catch (err) {
console.error('Error marking as read:',err);
}
},[onUnreadUpdate]);

const fetchMessages=useCallback(async ()=> {
if (!selectedPlan) return;
try {
const {data}=await supabase
 .from('trainer_messages')
 .select('*')
 .eq('plan_id',selectedPlan)
 .order('created_at',{ascending: true});
setMessages(data || []);
const hasUnread=data?.some(m=> !m.is_read && m.sender_type==='owner');
if (hasUnread) {
markAsRead(selectedPlan);
}
const videoSubmissions=data?.filter(m=> m.metadata?.task_id);
if (videoSubmissions?.length) {
const tIds=videoSubmissions.map(m=> m.metadata.task_id);
const {data: taskData}=await supabase.from('training_tasks').select('id,video_reviewed').in('id',tIds);
const taskMap={};
taskData?.forEach(t=> taskMap[t.id]=t.video_reviewed);
setTasks(taskMap);
}
} catch (err) {
console.error('Error fetching messages:',err);
}
},[selectedPlan,markAsRead]);

useEffect(()=> {
fetchPlans();
},[fetchPlans]);

useEffect(()=> {
if (selectedPlan) {
fetchMessages();
const channel=supabase
 .channel(`trainer_chat_v4_${selectedPlan}`)
 .on('postgres_changes',{event: '*',schema: 'public',table: 'trainer_messages',filter: `plan_id=eq.${selectedPlan}`},(payload)=> {
if (payload.eventType==='INSERT') {
setMessages(current=> {
if (current.some(m=> m.id===payload.new.id)) return current;
return [...current,payload.new];
});
if (payload.new.sender_type==='owner') {
markAsRead(selectedPlan);
}
} else if (payload.eventType==='UPDATE') {
setMessages(current=> current.map(m=> m.id===payload.new.id ? payload.new : m));
} else if (payload.eventType==='DELETE') {
setMessages(current=> current.filter(m=> m.id !==payload.old.id));
}
})
 .subscribe();
return ()=> {
supabase.removeChannel(channel);
};
}
},[selectedPlan,fetchMessages,markAsRead]);

useEffect(()=> {
setLocalUnreadMap(initialUnreadMap);
},[initialUnreadMap]);

useEffect(()=> {
messagesEndRef.current?.scrollIntoView({behavior: 'smooth'});
},[messages]);

const handleSendMessage=async (e)=> {
e.preventDefault();
if (!newMessage.trim() || !selectedPlan || sending) return;
const messageText=newMessage.trim();
setNewMessage('');
setSending(true);
markAsRead(selectedPlan);
const tempId=`temp-${Date.now()}`;
const optimisticMsg={id: tempId,plan_id: selectedPlan,sender_type: 'trainer',sender_name: trainerName,message_text: messageText,created_at: new Date().toISOString(),is_read: true,is_optimistic: true};
setMessages(prev=> [...prev,optimisticMsg]);
try {
const {data,error}=await supabase
 .from('trainer_messages')
 .insert([{plan_id: selectedPlan,sender_type: 'trainer',sender_name: trainerName,message_text: messageText,is_read: true}])
 .select()
 .single();
if (error) throw error;
setMessages(prev=> prev.map(m=> m.id===tempId ? data : m));
} catch (err) {
console.error('Failed to send message:',err);
setMessages(prev=> prev.filter(m=> m.id !==tempId));
setNewMessage(messageText);
} finally {
setSending(false);
}
};

const handleDeleteMessage=async (messageId)=> {
if (!window.confirm('Are you sure you want to delete this message?')) return;
setDeletingId(messageId);
try {
const {error}=await supabase
 .from('trainer_messages')
 .delete()
 .eq('id',messageId);
if (error) throw error;
} catch (err) {
console.error('Error deleting message:',err);
alert('Failed to delete message. You might not have permission.');
} finally {
setDeletingId(null);
}
};

const toggleTaskReview=async (taskId,current)=> {
const {error}=await supabase.from('training_tasks').update({video_reviewed: !current}).eq('id',taskId);
if (!error) {
setTasks(prev=> ({...prev,[taskId]: !current}));
}
};

if (loading) return <div className="h-96 animate-pulse bg-white rounded-xl" />;

return (
 <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 h-[700px]">
 <div className="lg:col-span-1 bg-white rounded-3xl border border-gray-100 p-4 space-y-2 overflow-y-auto">
 <h3 className="text-xs font-black text-gray-400 uppercase tracking-widest px-2 mb-4">Conversations</h3>
 {plans.map(p=> (
 <button key={p.id} onClick={()=> {setSelectedPlan(p.id);markAsRead(p.id);}} className={`relative w-full text-left p-4 rounded-2xl transition-all ${selectedPlan===p.id ? 'bg-indigo-50 border-2 border-indigo-200 shadow-sm' : 'hover:bg-gray-50 border-2 border-transparent'}`} >
 <div className="flex justify-between items-start">
 <p className="font-bold text-gray-900 text-sm">{p.training_clients?.dog_name}</p>
 {(localUnreadMap[p.id] > 0) && (
 <span className="bg-red-500 text-white text-[10px] px-1.5 py-0.5 rounded-full font-black animate-bounce shadow-sm">
 {localUnreadMap[p.id]}
 </span>
 )}
 </div>
 <p className="text-[10px] text-gray-500">{p.plan_name}</p>
 </button>
 ))}
 </div>
 <div className="lg:col-span-3 flex flex-col bg-white rounded-3xl border border-gray-100 overflow-hidden">
 <div className="p-6 border-b bg-gray-50/50 flex justify-between items-center">
 <div className="flex items-center gap-3">
 <div className="w-10 h-10 bg-indigo-600 rounded-full flex items-center justify-center text-white font-bold shadow-sm">
 {plans.find(p=> p.id===selectedPlan)?.training_clients?.dog_name?.charAt(0) || '?'}
 </div>
 <div>
 <h3 className="font-bold text-gray-900">
 {plans.find(p=> p.id===selectedPlan)?.training_clients?.dog_name || 'Select a dog'}
 </h3>
 <p className="text-[10px] text-gray-400 font-medium">Chatting with {plans.find(p=> p.id===selectedPlan)?.training_clients?.owner_name}</p>
 </div>
 </div>
 </div>
 <div className="flex-1 overflow-y-auto p-6 space-y-6 bg-gray-50/20">
 {messages.map(msg=> {const isVideo=msg.message_text?.startsWith('🎥');const isTrainer=msg.sender_type==='trainer';return (
 <div key={msg.id} className={`flex ${isTrainer ? 'justify-end' : 'justify-start'}`}>
 <div className={`max-w-[85%] flex flex-col ${isTrainer ? 'items-end' : 'items-start'} group relative`}>
 <button onClick={()=> handleDeleteMessage(msg.id)} disabled={deletingId===msg.id} className={`absolute ${isTrainer ? '-left-8' : '-right-8'} top-1/2 -translate-y-1/2 p-1.5 text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-all`} title="Delete message" >
 {deletingId===msg.id ? (
 <div className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
 ) : (
 <SafeIcon name="Trash2" className="w-4 h-4" />
 )}
 </button>
 {isVideo ? (
 <div className="bg-white border rounded-3xl overflow-hidden shadow-xl max-w-sm mt-2">
 <div className="aspect-video bg-black flex items-center justify-center relative">
 {processVideoUrl(msg.message_text.split(': ').slice(1).join(': ')) ? (
 <iframe 
  src={processVideoUrl(msg.message_text.split(': ').slice(1).join(': '))} 
  className="w-full h-full" 
  allow="fullscreen; picture-in-picture; encrypted-media"
  allowFullScreen 
 />
 ) : (
 <a href={msg.message_text.split(': ').slice(1).join(': ')} target="_blank" className="text-indigo-400 font-bold underline text-xs">External Video Link</a>
 )}
 </div>
 <div className="p-4 bg-gray-50 flex flex-col gap-3">
 <p className="text-sm font-black text-gray-800">{msg.message_text.split(': ')[0]}</p>
 <button onClick={()=> msg.metadata?.task_id && toggleTaskReview(msg.metadata.task_id,tasks[msg.metadata.task_id])} className={`w-full py-2.5 rounded-xl font-bold text-xs flex items-center justify-center gap-2 transition-all ${tasks[msg.metadata?.task_id] ? 'bg-green-100 text-green-700' : 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-md shadow-indigo-100'}`}>
 <SafeIcon name={tasks[msg.metadata?.task_id] ? "CheckCircle" : "Square"} /> {tasks[msg.metadata?.task_id] ? 'Reviewed' : 'Mark as Reviewed'}
 </button>
 </div>
 </div>
 ) : (
 <div className={`px-5 py-3 rounded-2xl text-sm shadow-sm relative ${isTrainer ? 'bg-indigo-600 text-white rounded-br-none' : 'bg-white border border-gray-200 text-gray-800 rounded-bl-none'} ${msg.is_optimistic ? 'opacity-70' : ''}`}>
 {msg.message_text} {!isTrainer && !msg.is_read && (
 <div className="absolute -left-2 top-1/2 -translate-y-1/2 w-1.5 h-1.5 bg-indigo-500 rounded-full" />
 )}
 </div>
 )}
 <div className="text-[10px] mt-2 text-gray-400 font-medium px-1">
 {msg.sender_name} • {format(new Date(msg.created_at),'h:mm a')}
 </div>
 </div>
 </div>
 );})}
 <div ref={messagesEndRef} />
 </div>
 <form onSubmit={handleSendMessage} className="p-6 border-t bg-white">
 <div className="flex gap-3">
 <input value={newMessage} onChange={e=> setNewMessage(e.target.value)} placeholder="Type a message to owner..." disabled={sending} className="flex-1 bg-gray-50 border-none rounded-2xl px-5 py-4 text-sm focus:ring-2 focus:ring-indigo-500 outline-none disabled:opacity-50" />
 <button type="submit" disabled={sending || !newMessage.trim() || !selectedPlan} className="bg-indigo-600 text-white p-4 rounded-2xl hover:bg-indigo-700 transition-colors shadow-lg shadow-indigo-100 disabled:opacity-50" >
 {sending ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <SafeIcon name="Send" />}
 </button>
 </div>
 </form>
 </div>
 </div>
 );
};
export default MessagingSection;